import { NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { BrowserModule } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { registerLocaleData, APP_BASE_HREF } from '@angular/common';
import en from '@angular/common/locales/en';
import lv from '@angular/common/locales/lv';
import { JwtHelperService, JwtModule } from '@auth0/angular-jwt';
import { firstValueFrom } from 'rxjs';

import { NZ_DATE_CONFIG, NZ_I18N, lv_LV } from 'ng-zorro-antd/i18n';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SharedModule } from '@cl/shared/shared.module';
import { AuthErrorInterceptor } from './interceptors/auth.interceptor';
import { AuthService } from '@cl/core/services/auth.service';
import { ThemeService } from '@cl/core/services/theme.service';
import { environment } from '@env/environment';
import { NotFoundComponent } from './not-found/not-found.component';
import { ESignatureComponent } from './components/e-signature/e-signature.component';

registerLocaleData(en);
lv[1][3] = '.'; // force decimal separator to be an dot (.) rather than comma (,) from LV locale
registerLocaleData(lv);
@NgModule({
    declarations: [AppComponent, NotFoundComponent, ESignatureComponent],
    bootstrap: [AppComponent],
    imports: [
        SharedModule,
        RouterModule,
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        JwtModule.forRoot({
            config: {
                tokenGetter: () => {
                    const name: string = 'auth-token';
                    const nameEQ: string = name + '=';
                    const ca: string[] = document.cookie.split(';');
                    for (let i: number = 0; i < ca.length; i++) {
                        const c: string = ca[i].trim();
                        if (c.indexOf(nameEQ) == 0) {
                            return c.substring(nameEQ.length);
                        }
                    }

                    return null;
                },
                allowedDomains: [environment.apiBaseUrl],
            },
        }),
    ],
    providers: [
        { provide: NZ_I18N, useValue: lv_LV },
        {
            provide: NZ_DATE_CONFIG,
            useValue: {
                firstDayOfWeek: 1,
            },
        },
        { provide: APP_BASE_HREF, useValue: '/' },
        {
            // block bootstrap until permissions are loaded and theme is applied
            provide: APP_INITIALIZER,
            useFactory: (auth: AuthService, theme: ThemeService, jwtHelper: JwtHelperService) => async (): Promise<void> => {
                try {
                    // If token exists but is expired, try to refresh first
                    if (auth.token && jwtHelper.isTokenExpired(auth.token)) {
                        await firstValueFrom(auth.callRefreshToken());
                        return;
                    }

                    await auth.loadPermissions();
                    const lastUsedTheme: number = parseInt(localStorage.getItem('uiTheme'), 10);
                    // awaited, so that AppComponent finds this theme already loaded and does not load its CSS a second time
                    await theme.switchTheme(lastUsedTheme ? lastUsedTheme : 0);
                } catch {
                    // Swallow errors (expired/invalid token → guard redirects normally)
                }
            },
            deps: [AuthService, ThemeService, JwtHelperService],
            multi: true,
        },
        // {
        //   provide: HTTP_INTERCEPTORS,
        //   useClass: BaseUrlInterceptor,
        //   multi: true
        // },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthErrorInterceptor,
            multi: true,
        },
        provideHttpClient(withInterceptorsFromDi()),
    ],
})
export class AppModule {}
